/**
 * A text's sign-in link (`/s/<token id>.<signature>`, .server/sms/links.ts)
 * is a credential in a URL. Wherever a URL is recorded by a third party (the
 * server's Sentry spans and transactions, the browser's Sentry errors,
 * breadcrumbs and replay events), the token is replaced with `:token` before
 * it leaves us.
 *
 * Only that link is touched: a path whose FIRST segment is `s`, followed by
 * a token in exactly the link's format (a 22-character id, a dot, a
 * 43-character signature, base64url), as a relative URL or after a host
 * (also percent-encoded inside another URL, `…%2F%2Fhost%2Fs%2F<token>`).
 * Any other `/s/` (fonts.gstatic.com/s/manrope/…, a deeper path) is left
 * exactly as it was. Pure and import-free: the browser bundle runs it too.
 */

const TOKEN = '[A-Za-z0-9_-]{22}\\.[A-Za-z0-9_-]{43}';

/**
 * `/s/<token>` as the first path segment: at the start, after a character a
 * URL path cannot continue from (a space, a quote, a bracket, `=`), or right
 * after a URL's authority (`//host[:port]`). Case-insensitive like the route.
 */
const TOKEN_IN_PATH = new RegExp(`(^|[\\s"'(<=,]|//[A-Za-z0-9.-]+(?::\\d+)?)/s/${TOKEN}(?![A-Za-z0-9_.-])`, 'gi');

/** The same, percent-encoded inside another URL's query: `%2F%2Fhost%2Fs%2F<token>`. */
const TOKEN_ENCODED = new RegExp(`(%2F%2F[A-Za-z0-9.-]+(?:%3A\\d+)?)%2Fs%2F${TOKEN}(?![A-Za-z0-9_.-])`, 'gi');

/**
 * A photographer's set-password link (`/welcome/<user id>.<link id>.<signature>`,
 * .server/provider-password-link.ts) is scrubbed the same way, to `/welcome/:token`.
 */
const WELCOME_TOKEN = '[A-Za-z0-9-]{1,64}\\.[A-Za-z0-9_-]{22}\\.[A-Za-z0-9_-]{43}';
const WELCOME_IN_PATH = new RegExp(
  `(^|[\\s"'(<=,]|//[A-Za-z0-9.-]+(?::\\d+)?)/welcome/${WELCOME_TOKEN}(?![A-Za-z0-9_.-])`,
  'gi',
);
const WELCOME_ENCODED = new RegExp(
  `(%2F%2F[A-Za-z0-9.-]+(?:%3A\\d+)?)%2Fwelcome%2F${WELCOME_TOKEN}(?![A-Za-z0-9_.-])`,
  'gi',
);

/** `text` with every text sign-in link's (and set-password link's) token replaced by `:token`. */
export function scrub_sms_link_tokens(text: string): string {
  let out = text;

  if (/(\/|%2F)welcome(\/|%2F)/i.test(out)) {
    out = out
      .replace(WELCOME_IN_PATH, (_match, before: string) => `${before}/welcome/:token`)
      .replace(WELCOME_ENCODED, (_match, before: string) => `${before}%2Fwelcome%2F:token`);
  }

  if (!/(\/|%2F)s(\/|%2F)/i.test(out)) {
    return out;
  }

  return out
    .replace(TOKEN_IN_PATH, (_match, before: string) => `${before}/s/:token`)
    .replace(TOKEN_ENCODED, (_match, before: string) => `${before}%2Fs%2F:token`);
}

/**
 * `value` with every string in it scrubbed, in place (objects and arrays are
 * walked; anything else is returned as it is). For a Sentry event, span or
 * breadcrumb, or a replay recording event, before it is sent.
 */
export function scrub_sms_link_deep<T>(value: T): T {
  return walk(value, 0, new WeakSet()) as T;
}

function walk(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrub_sms_link_tokens(value);
  }

  if (value === null || typeof value !== 'object' || depth > 24 || seen.has(value)) {
    return value;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const next = walk(value[i], depth + 1, seen);

      if (next !== value[i]) {
        value[i] = next;
      }
    }

    return value;
  }

  const record = value as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    const next = walk(record[key], depth + 1, seen);

    if (next !== record[key]) {
      try {
        record[key] = next;
      } catch {
        // A read-only field: left as it is.
      }
    }
  }

  return value;
}

/** Whether a page is a text's sign-in link page, or a set-password link page (case-insensitive, as Remix routes it). */
export function is_sms_link_path(pathname: string): boolean {
  return /^\/(s|welcome)\//i.test(pathname);
}

/**
 * A Sentry event processor (Sentry.addEventProcessor): every event the SDK
 * prepares, the replay's own `replay_event` included (its `urls` list of
 * visited pages and its `request.url`, which never pass through beforeSend or
 * beforeAddRecordingEvent), scrubbed before it is sent.
 */
export function scrub_sentry_event<T>(event: T): T {
  return scrub_sms_link_deep(event);
}
